import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Sparkles, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SettingsContext';

/**
 * Slim upsell strip on the homepage nudging non-members to grab a membership.
 * Hides itself when:
 *  - Memberships aren't published yet (subscriptions_visible === false)
 *  - The signed-in user already has an active membership
 */
export default function MembershipUpsell() {
  const { user } = useAuth();
  const { settings } = useSiteSettings();

  const visible = !!settings?.subscriptions_visible;
  const activeMember = !!user?.membership && new Date(user.membership.expires_at).getTime() > Date.now();
  // If the user is already Pro, no upsell. If they're Basic, nudge towards Pro. If logged out, nudge to sign up.
  const tier = activeMember ? (user.membership.tier || 'basic') : null;
  if (tier === 'pro') return null;

  const copy = tier === 'basic'
    ? {
        tag: 'Upgrade',
        title: 'Go Pro for bigger discounts & priority service.',
        body: 'You already get 10% off parts & free delivery. Pro members get bigger savings and jump the service queue.',
        cta: 'See Pro plans',
      }
    : {
        tag: 'Members only',
        title: 'Get 10% off parts & free delivery — just sign up.',
        body: 'Every registered customer gets Basic Member perks automatically. We even cover Knutsford islandwide.',
        cta: user ? 'See membership plans' : 'Sign in — free',
      };

  // Hide only when memberships module is turned off AND the user isn't logged out
  // (logged-out customers can still sign up and unlock Basic perks).
  if (!visible && user) return null;

  return (
    <section className="bg-black text-white border-y border-white/10" data-testid="membership-upsell">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 sm:py-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-[#e11d2c] text-white grid place-items-center shrink-0">
            <Truck size={20} />
          </div>
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-bold text-[#e11d2c]">
              <Sparkles size={11} /> {copy.tag}
            </div>
            <div className="font-display text-[16px] sm:text-[18px] font-bold leading-tight mt-1">
              {copy.title}
            </div>
            <p className="text-white/60 text-[12.5px] mt-1 leading-relaxed">
              {copy.body}
            </p>
          </div>
        </div>
        <Link
          to={tier === 'basic' ? '/memberships' : (user ? '/memberships' : '/login')}
          data-testid="membership-upsell-cta"
          className="sm:ml-auto inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#e11d2c] hover:bg-[#c01526] text-white font-semibold text-[13px] whitespace-nowrap transition-colors shrink-0"
        >
          {copy.cta} <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}
